import { useEffect, useState } from 'react';
import { FilePlus, Pencil, Trash2, RefreshCw, CreditCard, ArrowRightLeft } from 'lucide-react';
import Layout from '../components/Layout';
import { api } from '../lib/api';

const ENTITY_ICONS = {
  invoice: FilePlus,
  client: FilePlus,
  payment: CreditCard,
  bank_account: CreditCard,
  recurring_invoice: RefreshCw,
};

const ACTION_COLORS = {
  create: '#2A4A35',
  update: '#B8A96A',
  status_change: '#B8A96A',
  delete: '#5C1A2A',
};

const ENTITY_LABELS = {
  invoice: 'Invoice',
  client: 'Client',
  payment: 'Payment',
  bank_account: 'Bank Account',
  recurring_invoice: 'Recurring Template',
};

const Audit = () => {
  const [logs, setLogs] = useState([]);
  const [entityFilter, setEntityFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchLogs = () => {
    setLoading(true);
    const params = entityFilter ? { entity_type: entityFilter, limit: 200 } : { limit: 200 };
    api.get('/audit-log', { params }).then(r => {
      setLogs(r.data);
      setLoading(false);
    });
  };

  useEffect(() => { queueMicrotask(() => fetchLogs()); }, [entityFilter]);

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const renderChange = (action, changes) => {
    if (action === 'status_change' && changes.from && changes.to) {
      return (
        <span className="inline-flex items-center font-body text-xs">
          <span className="px-2 py-0.5 border border-border uppercase tracking-widest mr-2">{changes.from}</span>
          <ArrowRightLeft className="w-3 h-3 text-text-secondary" strokeWidth={1} />
          <span className="px-2 py-0.5 border border-maroon text-maroon uppercase tracking-widest ml-2">{changes.to}</span>
        </span>
      );
    }
    if (changes && Object.keys(changes).length > 0) {
      return <span className="font-body text-xs text-text-secondary mono-num">{JSON.stringify(changes).slice(0, 80)}</span>;
    }
    return null;
  };

  const filterButtons = [
    { id: '', label: 'All' },
    { id: 'invoice', label: 'Invoices' },
    { id: 'client', label: 'Clients' },
    { id: 'payment', label: 'Payments' },
    { id: 'bank_account', label: 'Banks' },
    { id: 'recurring_invoice', label: 'Recurring' },
  ];

  return (
    <Layout title="Audit Log">
      <div className="bg-surface border border-border p-6 fade-up">
        <div className="flex justify-between items-center mb-6 pb-6 border-b border-border">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary">Activity Trail</p>
            <h2 className="font-heading text-xl font-light text-text-primary">All Changes · {logs.length} entries</h2>
          </div>
          <button data-testid="refresh-audit" onClick={fetchLogs} className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover flex items-center transition-colors">
            <RefreshCw className="w-3.5 h-3.5 mr-1" strokeWidth={1} /> Refresh
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {filterButtons.map(f => (
            <button
              key={f.id || 'all'}
              data-testid={`audit-filter-${f.id || 'all'}`}
              onClick={() => setEntityFilter(f.id)}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest font-body border transition-colors ${entityFilter === f.id ? 'bg-maroon text-white border-maroon' : 'bg-transparent text-text-secondary border-border hover:border-maroon hover:text-maroon'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center py-12 font-body text-sm text-text-secondary">Loading...</p>
        ) : logs.length === 0 ? (
          <p className="text-center py-12 font-body text-sm text-text-secondary">No activity logged yet</p>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
            
            <div className="space-y-1">
              {logs.map((log, i) => {
                const Icon = ENTITY_ICONS[log.entity_type] || FilePlus;
                const color = ACTION_COLORS[log.action] || '#4A4A4A';
                return (
                  <div key={log.id} data-testid={`audit-row-${log.id}`} className="relative pl-12 pr-3 py-3 hover:bg-cream transition-colors">
                    <div className="absolute left-2 top-4 w-4 h-4 bg-cream border flex items-center justify-center" style={{ borderColor: color }}>
                      <Icon className="w-2.5 h-2.5" strokeWidth={1.5} style={{ color }} />
                    </div>
                    
                    <div className="flex justify-between items-baseline gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-3 mb-1 flex-wrap">
                          <span className="font-body text-xs uppercase tracking-[0.2em]" style={{ color }}>
                            {log.action.replace('_', ' ')}
                          </span>
                          <span className="font-body text-xs uppercase tracking-widest text-text-secondary">{ENTITY_LABELS[log.entity_type] || log.entity_type}</span>
                          <span className="font-body text-sm text-text-primary font-numbers mono-num truncate">{log.entity_label || log.entity_id?.slice(0, 8) || '—'}</span>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-body text-xs text-text-secondary">by {log.actor}</span>
                          {renderChange(log.action, log.changes)}
                        </div>
                      </div>
                      <span className="font-body text-xs text-text-secondary mono-num flex-shrink-0">{formatTime(log.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Audit;
